import { useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import Preloader from 'components/Preloader';
import { usePosPromotions } from 'pages/PosSettings/hooks/usePosPromotions';
import { MODE, MUI_TO_SX_STYLES } from 'pages/TableOrdering/config';
import { StripedDataGrid } from 'pages/TableOrdering/styles';
import CreateOrEditForm from 'pages/PosSettings/Promotions/CreateOrEditForm';
import { ACTION_SCOPES } from 'pages/PosSettings/Promotions/promotionAction';

const getDetail = (promo) => {
  if (promo?.promotionDetail) return promo.promotionDetail;
  return {
    actions: promo?.actions || promo?._actions,
  };
};

const PromotionTab = ({
  lockedProductIds,
  lockedCategoryIds,
  lockedProductName,
  lockedCategoryName,
}) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    handleCancel: hookCancel,
    formMethods,
    promotionColumns,
    promotionRows,
    loading,
    open,
    startCreate,
    mode,
    productOptions,
    categoryOptions,
  } = usePosPromotions();

  const filteredRows = useMemo(() => {
    if (!promotionRows) return [];
    return promotionRows.filter((row) => {
      const promo = row._raw;
      const d = getDetail(promo);
      const actions = d?.actions || [];
      for (const action of actions) {
        if (
          lockedProductIds?.length &&
          action.targetItemUuids?.some((id) => lockedProductIds.includes(id))
        ) {
          return true;
        }
        if (
          lockedCategoryIds?.length &&
          action.targetCategoryUuids?.some((id) =>
            lockedCategoryIds.includes(id)
          )
        ) {
          return true;
        }
      }
      return false;
    });
  }, [promotionRows, lockedProductIds, lockedCategoryIds]);

  // A promotion created from a menu item or category targets that item or
  // category, so it is item level whatever the type would otherwise allow.
  const locked = !!(lockedProductIds?.length || lockedCategoryIds?.length);

  const applyLockedTargets = () => {
    formMethods.setValue('actionScope', ACTION_SCOPES.item);
    if (lockedProductIds?.length) {
      formMethods.setValue('actionTargetItemUuids', lockedProductIds);
    }
    if (lockedCategoryIds?.length) {
      formMethods.setValue('actionTargetCategoryUuids', lockedCategoryIds);
    }
  };

  useEffect(() => {
    if (open && mode === MODE.CREATE) applyLockedTargets();
  }, [open, mode]);

  // The locked targets are applied by the effect above once the dialog opens.
  const handleAdd = () => startCreate();

  const onSubmit = (values) => {
    if (locked) values.actionScope = ACTION_SCOPES.item;
    if (lockedProductIds?.length && mode === MODE.CREATE) {
      values.actionTargetItemUuids = lockedProductIds;
    }
    if (lockedCategoryIds?.length && mode === MODE.CREATE) {
      values.actionTargetCategoryUuids = lockedCategoryIds;
    }
    handleSubmit(values);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          onClick={handleAdd}
          variant="contained"
          color="primary"
          type="button"
          disabled={open}
          startIcon={<AddOutlinedIcon />}
        >
          {t('Add Promotion')}
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={hookCancel}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown
        PaperProps={{ sx: { maxHeight: '90vh', background: '#FAFAFA' } }}
      >
        <FormProvider {...formMethods}>
          <form
            noValidate
            onSubmit={formMethods.handleSubmit(onSubmit)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
            }}
          >
            <DialogTitle sx={{ fontSize: '24px', fontWeight: 400, pr: 6 }}>
              {mode === MODE.CREATE
                ? t('Add Promotion')
                : t('Edit Promotion')}
              <IconButton
                aria-label="close"
                onClick={hookCancel}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                  color: '#929398',
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
              <CreateOrEditForm
                productOptions={productOptions}
                categoryOptions={categoryOptions}
                lockedProductIds={lockedProductIds}
                lockedCategoryIds={lockedCategoryIds}
                lockedProductName={lockedProductName}
                lockedCategoryName={lockedCategoryName}
              />
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid #E7E7E7', p: 2 }}>
              <Button variant="text" onClick={hookCancel}>
                {t('Cancel')}
              </Button>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={loading}
                // Submitting an invalid form marks every problem instead of
                // leaving a dead button with nothing to explain it.
                disabled={loading || !formMethods.formState.isDirty}
              >
                {mode === MODE.CREATE
                  ? t('Add Promotion')
                  : t('table_ordering.save')}
              </LoadingButton>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>

      {filteredRows.length > 0 ? (
        <StripedDataGrid
          sx={MUI_TO_SX_STYLES}
          autoHeight={true}
          hideFooterSelectedRowCount={true}
          hideFooter={true}
          isEditable={false}
          rowsPerPageOptions={[100]}
          pageSizeOptions={[100]}
          pageSize={100}
          rows={filteredRows}
          columns={promotionColumns}
          components={{
            LoadingOverlay: Preloader,
          }}
          loading={loading}
        />
      ) : !loading ? (
        <Box sx={{ textAlign: 'center', py: 4, color: '#9E9EAD' }}>
          {t('There are no promotions yet')}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Preloader />
        </Box>
      )}
    </Box>
  );
};

export default PromotionTab;
